import React, { useMemo, useRef, useEffect, useLayoutEffect, useState } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Plus, Copy, Trash2, Search, MapPin, ArrowRight, Columns3, X, AlertTriangle, Filter, FileText } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useI18n } from "../context/LanguageContext";
import { fillSeries } from "../lib/series";
import { getColumnOptions } from "../lib/options";
import { ComboCell } from "./ComboCell";
import { readClipboardGrid, parseNumber, headerMapping } from "../lib/clipboardGrid";
import { cellValue, rowArea, selectionStats } from "../lib/cellValue";
import { SelectionStats } from "./SelectionStats";
import { OffsetInput } from "./OffsetInput";
import { ColumnFilter, BLANK } from "./ColumnFilter";
import { history } from "../lib/history";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "./ui/alert-dialog";

const AI_COL_ACCENT = { blindPos: "#eab308", width: "#3b82f6", height: "#22c55e", qty: "#ef4444" };
const BLIND_ACCENT = "#14b8a6";

export default function OrderTable({
    rows, setRows, rowColors, activeRowId, setActiveRowId, onHistoryBreak,
    search, setSearch, typeFilter, setTypeFilter,
    onAddRow, makeRow, onCopyRow, onDeleteRow, unitOffsets, trayPage, onGoToPage, onLocate, onDeck, flashIds, onClearFlashes, pageNames, paused, onTogglePaused,
    columns: allColumns, setColumns, blindOffsets, setBlindOffsets, onVisibleChange,
}) {
    const { t } = useI18n();
    const columns = useMemo(() => allColumns.filter((c) => !c.hidden), [allColumns]);
    const [pagesOpen, setPagesOpen] = useState(null);
    const hiddenCols = allColumns.filter((c) => c.hidden);
    const rowRefs = useRef({});
    const dragStateRef = useRef(null);
    const resizeRef = useRef(null);
    const pendingFocusColRef = useRef(null);
    const [editingKey, setEditingKey] = useState(null);
    const [showFilters, setShowFilters] = useState(true);
    const [dragKey, setDragKey] = useState(null);
    const [overIdx, setOverIdx] = useState(null);

    const commitLabel = (key, val) => {
        const v = (val || "").trim();
        setColumns((prev) => prev.map((c) => (c.key === key ? { ...c, label: v || c.label } : c)));
        setEditingKey(null);
    };

    useEffect(() => {
        if (activeRowId && rowRefs.current[activeRowId]) {
            rowRefs.current[activeRowId].scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [activeRowId]);

    // Column resize (drag the right border of a header cell)
    useEffect(() => {
        const move = (e) => {
            const r = resizeRef.current;
            if (!r) return;
            const w = Math.max(56, r.startW + (e.clientX - r.startX));
            setColumns((prev) => prev.map((c) => (c.key === r.key ? { ...c, width: w } : c)));
        };
        const up = () => { resizeRef.current = null; };
        window.addEventListener("mousemove", move);
        window.addEventListener("mouseup", up);
        return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    }, [setColumns]);

    const types = useMemo(() => {
        const s = new Set();
        rows.forEach((r) => r.type && s.add(r.type));
        return Array.from(s);
    }, [rows]);

    const effOff = (r, k) => Number(r[k === "W" ? "blindOffW" : "blindOffH"] ?? (k === "W" ? blindOffsets?.width : blindOffsets?.height) ?? 0) || 0;
    const hasPos = (r) => r.width || r.height || r.blindPos;
    const corrCount = useMemo(() => ({
        W: rows.filter((r) => hasPos(r) && effOff(r, "W") !== 0).length,
        H: rows.filter((r) => hasPos(r) && effOff(r, "H") !== 0).length,
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [rows, blindOffsets]);
    const [onlyCorrected, setOnlyCorrected] = useState(false);
    const [valFilters, setValFiltersRaw] = useState({});
    const setValFilters = (next) => {
        const prev = valFilters;
        const n = typeof next === "function" ? next(prev) : next;
        setValFiltersRaw(n);
        if (JSON.stringify(n) === JSON.stringify(prev)) return;
        history.push({ label: t("h_filter"), undo: () => setValFiltersRaw(prev), redo: () => setValFiltersRaw(n) });
    };
    const fval = (c, r) => { const v = cellValue(c, r, blindOffsets); const s = v == null ? "" : String(v).trim(); return s === "" ? BLANK : s; };
    const passVals = (r, skipKey) => {
        for (const c of columns) {
            const s = valFilters[c.key];
            if (s && c.key !== skipKey && !s.has(fval(c, r))) return false;
        }
        return true;
    };
    const filterValues = (c) => {
        const m = new Map();
        baseRows.forEach((r) => { if (passVals(r, c.key)) { const v = fval(c, r); m.set(v, (m.get(v) || 0) + 1); } });
        return [...m].map(([v, n]) => ({ v, n }));
    };
    const activeValFilters = columns.filter((c) => valFilters[c.key]).length;
    // Excel-like: the filter is applied as a snapshot; edited rows stay visible and new rows are shown until the filter is changed.
    const liveRef = useRef({});
    liveRef.current = { rows, passVals };
    const valSnap = useMemo(() => {
        const { rows: rs, passVals: pv } = liveRef.current;
        return { known: new Set(rs.map((r) => r.id)), pass: new Set(rs.filter((r) => pv(r)).map((r) => r.id)) };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valFilters]);

    const baseRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        return rows.filter((r) => {
            if (onlyCorrected && !(effOff(r, "W") !== 0 || effOff(r, "H") !== 0)) return false;
            if (typeFilter !== "__all__" && r.type !== typeFilter) return false;
            if (!q) return true;
            return columns.map((c) => String(r[c.key] ?? "").toLowerCase()).some((v) => v.includes(q));
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, search, typeFilter, columns, onlyCorrected, blindOffsets]);
    const filtered = useMemo(() => (activeValFilters === 0 ? baseRows
        : baseRows.filter((r) => !valSnap.known.has(r.id) || valSnap.pass.has(r.id))), [baseRows, valSnap, activeValFilters]);

    useEffect(() => {
        if (onVisibleChange) onVisibleChange(filtered.map((r) => r.id));
    }, [filtered, onVisibleChange]);

    const update = (id, key, value, type) => {
        setRows((prev) => prev.map((r) => {
            if (r.id !== id) return r;
            let v = value;
            if (key === "width" || key === "height") v = value === "" ? null : Number(value);
            else if (key === "qty") v = value === "" ? 0 : Math.max(0, parseInt(value, 10) || 0);
            else if (type === "number") v = value === "" ? "" : value;
            return { ...r, [key]: v };
        }));
    };

    // --- Excel-like selection, clipboard and fill handle ---
    const dataKey = (c) => (c.calc === "blindW" ? "blindOffW" : c.calc === "blindH" ? "blindOffH" : c.key);
    const coerce = (c, value) => {
        const k = dataKey(c);
        if (k === "qty") return Math.max(0, Math.round(parseNumber(value) ?? 0));
        if (c.calc || k === "width" || k === "height" || c.type === "number") return parseNumber(value);
        return typeof value === "string" ? value.trim() : (value ?? "");
    };
    const [sel, setSel] = useState(null);
    const [copied, setCopied] = useState(null);
    const [antsBox, setAntsBox] = useState(null);
    const tableRef = useRef(null);
    const focusTable = () => { try { tableRef.current?.focus({ preventScroll: true }); } catch { /* ignore */ } };
    const isBlindCol = (col) => col?.calc === "blindW" || col?.calc === "blindH";
    const [pendingPaste, setPendingPaste] = useState(null);
    const ctrlARef = useRef(null);
    const selectingRef = useRef(false);
    const fillRef = useRef(null);
    const [fillTo, setFillTo] = useState(null);
    const rect = sel ? { r0: Math.min(sel.ar, sel.fr), r1: Math.max(sel.ar, sel.fr), c0: Math.min(sel.ac, sel.fc), c1: Math.max(sel.ac, sel.fc) } : null;
    const isMulti = !!rect && (rect.r0 !== rect.r1 || rect.c0 !== rect.c1);
    const inRect = (rc, r, c) => !!rc && r >= rc.r0 && r <= rc.r1 && c >= rc.c0 && c <= rc.c1;
    const fillRect = rect && fillTo != null
        ? (fillTo > rect.r1 ? { ...rect, r0: rect.r1 + 1, r1: fillTo } : fillTo < rect.r0 ? { ...rect, r0: fillTo, r1: rect.r0 - 1 } : null)
        : null;

    const writeCells = (allWrites, allowExtra = true) => {
        const baseIds = filtered.map((r) => r.id);
        const writes = allowExtra ? allWrites : allWrites.filter((w) => w.r < baseIds.length);
        if (!writes.length) return;
        onHistoryBreak?.();
        setTimeout(() => onHistoryBreak?.(), 0);
        const extra = Math.max(...writes.map((w) => w.r)) - (baseIds.length - 1);
        setRows((prev) => {
            const next = [...prev];
            const ids = [...baseIds];
            for (let k = 0; k < extra; k++) { const nr = makeRow(next); next.push(nr); ids.push(nr.id); }
            const patch = {}, abs = {};
            writes.forEach(({ r, c, v }) => {
                const col = columns[c];
                if (!col || !ids[r] || col.calc === "area" || col.key === "position") return;
                const n = isBlindCol(col) ? parseNumber(v) : null;
                // Blind columns: values > 200 are absolute blind sizes (as copied), smaller ones are corrections.
                if (n != null && Math.abs(n) > 200) { (abs[ids[r]] = abs[ids[r]] || {})[dataKey(col)] = n; return; }
                (patch[ids[r]] = patch[ids[r]] || {})[dataKey(col)] = coerce(col, v);
            });
            Object.entries(abs).forEach(([id, vals]) => {
                const row = next.find((x) => x.id === id) || {};
                Object.entries(vals).forEach(([k, n]) => {
                    const base = Number((patch[id] || {})[k === "blindOffW" ? "width" : "height"] ?? row[k === "blindOffW" ? "width" : "height"]);
                    if (Number.isFinite(base) && base > 0) (patch[id] = patch[id] || {})[k] = n - base;
                });
            });
            return next.map((row) => (patch[row.id] ? { ...row, ...patch[row.id] } : row));
        });
    };
    const rectWrites = (rc, valueAt) => {
        const w = [];
        for (let r = rc.r0; r <= rc.r1; r++) for (let c = rc.c0; c <= rc.c1; c++) w.push({ r, c, v: valueAt(r, c) });
        return w;
    };
    const clearRect = (rc) => writeCells(rectWrites(rc, () => ""));
    const cellText = (r, c) => {
        const col = columns[c], row = filtered[r] || {};
        const v = col?.calc === "area" ? rowArea(row) : isBlindCol(col) ? cellValue(col, row, blindOffsets) : row[dataKey(col)];
        return v == null ? "" : String(v);
    };
    const wholeSelected = (el) => {
        try {
            const s = el.selectionStart, e2 = el.selectionEnd;
            return s == null || s === e2 || (s === 0 && e2 === (el.value || "").length);
        } catch { return true; }
    };

    const handleCopy = (e, cut) => {
        const onTable = e.target === tableRef.current;
        if (!rect || (!e.target.dataset?.cell && !onTable)) return;
        if (!isMulti && !onTable && !wholeSelected(e.target)) return;
        e.preventDefault();
        const lines = [];
        for (let r = rect.r0; r <= rect.r1; r++) {
            const cells = [];
            for (let c = rect.c0; c <= rect.c1; c++) cells.push(cellText(r, c));
            lines.push(cells.join("\t"));
        }
        e.clipboardData.setData("text/plain", lines.join("\r\n"));
        setCopied(cut ? null : { ...rect });
        if (cut) clearRect(rect);
    };
    const applyPaste = (writes, target, extra) => {
        writeCells(writes, extra);
        setSel({ ar: target.r0, ac: target.c0, fr: extra ? target.r1 : Math.min(target.r1, filtered.length - 1), fc: target.c1 });
        setPendingPaste(null);
    };
    const handlePaste = (e) => {
        const onTable = e.target === tableRef.current;
        const td = e.target.dataset?.cell && e.target.closest("[data-rc]");
        if (!td && !(onTable && rect)) return;
        const [pr, pc] = td ? td.dataset.rc.split(":").map(Number) : [rect.r0, rect.c0];
        const rc = rect && inRect(rect, pr, pc) ? rect : { r0: pr, r1: pr, c0: pc, c1: pc };
        const multiSel = rc.r0 !== rc.r1 || rc.c0 !== rc.c1;
        const grid = readClipboardGrid(e.clipboardData);
        if (!grid) return;
        const numeric = columns[pc] && (columns[pc].calc || columns[pc].type === "number");
        if (!onTable && grid.length === 1 && grid[0].length === 1 && !multiSel && !numeric && !wholeSelected(e.target)) return;
        e.preventDefault();
        let writes, target;
        const hdr = grid.length > 1 ? headerMapping(grid[0], columns) : null;
        if (hdr) {
            const body = grid.slice(1), cs = hdr.filter((i) => i >= 0);
            writes = body.flatMap((row, i) => hdr.map((ci, j) => (ci >= 0 ? { r: rc.r0 + i, c: ci, v: row[j] ?? "" } : null)).filter(Boolean));
            target = { r0: rc.r0, r1: rc.r0 + body.length - 1, c0: Math.min(...cs), c1: Math.max(...cs) };
        } else {
            const h = grid.length, w = grid[0].length;
            const sh = rc.r1 - rc.r0 + 1, sw = rc.c1 - rc.c0 + 1;
            const H = sh % h === 0 ? sh : h;
            const W = Math.min(sw % w === 0 ? sw : w, columns.length - rc.c0);
            target = { r0: rc.r0, r1: rc.r0 + H - 1, c0: rc.c0, c1: rc.c0 + W - 1 };
            writes = rectWrites(target, (r, c) => grid[(r - rc.r0) % h][(c - rc.c0) % w] ?? "");
        }
        const need = target.r1 - (filtered.length - 1);
        if (need > 0) setPendingPaste({ writes, target, need });
        else applyPaste(writes, target, true);
    };

    const onCellMouseDown = (e, r, c) => {
        if (e.button !== 0) return;
        const inField = e.target.closest("input,textarea,select,button,[role=combobox]");
        if (!inField) { e.preventDefault(); focusTable(); }
        if (e.shiftKey && sel) { e.preventDefault(); setSel((s) => ({ ...s, fr: r, fc: c })); return; }
        setSel({ ar: r, ac: c, fr: r, fc: c });
        selectingRef.current = true;
    };
    const lastRow = Math.max(0, filtered.length - 1), lastCol = Math.max(0, columns.length - 1);
    const selectColumn = (c, extend) => { focusTable(); setSel((s) => ({ ar: 0, ac: extend && s ? s.ac : c, fr: lastRow, fc: c })); };
    const selectRowAt = (r, extend) => { focusTable(); setSel((s) => ({ ar: extend && s ? s.ar : r, ac: 0, fr: r, fc: lastCol })); };
    // Excel "marching ants" around the copied range; cleared by Esc, a new copy, undo/redo or leaving the table.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") setCopied(null);
            else if ((e.ctrlKey || e.metaKey) && ["z", "y"].includes(e.key.toLowerCase())) setCopied(null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);
    useLayoutEffect(() => {
        const cont = tableRef.current;
        if (!copied || !cont) { setAntsBox(null); return; }
        const a = cont.querySelector(`[data-rc="${copied.r0}:${copied.c0}"]`);
        const b = cont.querySelector(`[data-rc="${Math.min(copied.r1, lastRow)}:${Math.min(copied.c1, lastCol)}"]`);
        if (!a || !b) { setAntsBox(null); return; }
        const cr = cont.getBoundingClientRect(), ra = a.getBoundingClientRect(), rb = b.getBoundingClientRect();
        const box = { left: ra.left - cr.left + cont.scrollLeft, top: ra.top - cr.top + cont.scrollTop, width: rb.right - ra.left, height: rb.bottom - ra.top };
        setAntsBox((p) => (p && p.left === box.left && p.top === box.top && p.width === box.width && p.height === box.height ? p : box));
    }, [copied, lastRow, lastCol, filtered, columns]);
    const headerClickRef = useRef(null);
    headerClickRef.current = selectColumn;
    const stats = useMemo(() => {
        if (!rect) return null;
        const vals = [];
        for (let r = rect.r0; r <= Math.min(rect.r1, filtered.length - 1); r++)
            for (let c = rect.c0; c <= rect.c1; c++) if (columns[c]) vals.push(cellValue(columns[c], filtered[r], blindOffsets));
        return selectionStats(vals);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sel, filtered, columns, blindOffsets]);
    const onCellMouseEnter = (r, c) => {
        if (fillRef.current) { setFillTo(r); return; }
        if (!selectingRef.current) return;
        setSel((s) => (s && (s.fr !== r || s.fc !== c) ? { ...s, fr: r, fc: c } : s));
        try { window.getSelection()?.removeAllRanges(); } catch { /* ignore */ }
    };
    const startFill = (e) => {
        e.preventDefault(); e.stopPropagation();
        fillRef.current = rect;
        setFillTo(rect.r1);
    };
    useEffect(() => {
        const up = () => {
            selectingRef.current = false;
            const src = fillRef.current;
            fillRef.current = null;
            if (!src || fillTo == null) { setFillTo(null); return; }
            const down = fillTo > src.r1;
            const count = down ? fillTo - src.r1 : src.r0 - fillTo;
            if (count > 0) {
                const writes = [];
                for (let c = src.c0; c <= src.c1; c++) {
                    const vals = [];
                    for (let r = src.r0; r <= src.r1; r++) vals.push(filtered[r]?.[dataKey(columns[c])] ?? "");
                    fillSeries(vals, count, down ? 1 : -1).forEach((v, k) => writes.push({ r: down ? src.r1 + 1 + k : src.r0 - 1 - k, c, v }));
                }
                writeCells(writes);
                setSel({ ar: down ? src.r0 : src.r1, ac: src.c0, fr: fillTo, fc: src.c1 });
            }
            setFillTo(null);
        };
        window.addEventListener("mouseup", up);
        return () => window.removeEventListener("mouseup", up);
    });

    const cellDeco = (idx, colIdx, baseShadow) => {
        const sh = baseShadow ? [baseShadow] : [];
        let cls = "";
        if (inRect(rect, idx, colIdx)) {
            const B = "#3b82f6";
            if (idx === rect.r0) sh.push(`inset 0 2px 0 0 ${B}`);
            if (idx === rect.r1) sh.push(`inset 0 -2px 0 0 ${B}`);
            if (colIdx === rect.c0) sh.push(`inset 2px 0 0 0 ${B}`);
            if (colIdx === rect.c1) sh.push(`inset -2px 0 0 0 ${B}`);
            if (isMulti && !(idx === sel.ar && colIdx === sel.ac)) cls = "bg-blue-500/20";
        } else if (inRect(fillRect, idx, colIdx)) {
            cls = "bg-blue-500/10 outline-dashed outline-1 outline-blue-400 -outline-offset-1";
        }
        return { cls, style: sh.length ? { boxShadow: sh.join(", ") } : {} };
    };
    const cellProps = (idx, colIdx) => ({
        "data-rc": `${idx}:${colIdx}`,
        onMouseDown: (e) => onCellMouseDown(e, idx, colIdx),
        onMouseEnter: () => onCellMouseEnter(idx, colIdx),
    });
    const collapseTo = (idx, colIdx) => { if (isMulti) setSel({ ar: idx, ac: colIdx, fr: idx, fc: colIdx }); };
    const fillHandle = (idx, colIdx, c, r) => rect && !fillRef.current && idx === rect.r1 && colIdx === rect.c1 && (
        <div data-testid={`fill-handle-${c.key}-${r.id}`} onMouseDown={startFill} title={t("ot_fill_down")}
            className="absolute -bottom-[4px] -right-[4px] w-2 h-2 bg-blue-500 border border-white cursor-crosshair z-20" />
    );

    const setRowOffset = (id, key, value) => {
        const v = value === "" || value == null ? null : Number(value);
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: (v === null || Number.isNaN(v)) ? null : v } : r)));
    };

    // --- Excel-like keyboard navigation between cells ---
    const cellTestId = (c, rowId) =>
        c.calc === "blindW" ? `blind-offw-${rowId}` : c.calc === "blindH" ? `blind-offh-${rowId}` : `cell-${c.key}-${rowId}`;

    const focusCell = (rowIdx, colIdx) => {
        if (rowIdx < 0 || colIdx < 0 || colIdx >= columns.length) return false;
        const row = filtered[rowIdx];
        if (!row) return false;
        const el = document.querySelector(`[data-testid="${cellTestId(columns[colIdx], row.id)}"]`);
        if (el) {
            el.focus();
            try { el.select && el.select(); } catch { /* number inputs */ }
            setActiveRowId(row.id);
            setSel({ ar: rowIdx, ac: colIdx, fr: rowIdx, fc: colIdx });
            return true;
        }
        return false;
    };

    const handleCellKeyDown = (e, rowIdx, colIdx) => {
        const el = e.target;
        const isNum = el.type === "number";
        const len = (el.value ?? "").length;
        const s0 = el.selectionStart, s1 = el.selectionEnd;
        const allSel = s0 === 0 && s1 === len;
        const atStart = isNum || (s0 === 0 && (s1 === 0 || allSel));
        const atEnd = isNum || (s1 === len && (s0 === len || allSel));
        const addNewAndFocus = (col) => { pendingFocusColRef.current = col; onAddRow(); };
        const isLast = rowIdx >= filtered.length - 1;
        if (e.shiftKey && e.key.startsWith("Arrow")) {
            e.preventDefault();
            const d = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] }[e.key];
            setSel((s) => {
                const b = s || { ar: rowIdx, ac: colIdx, fr: rowIdx, fc: colIdx };
                return {
                    ...b,
                    fr: Math.min(filtered.length - 1, Math.max(0, b.fr + d[0])),
                    fc: Math.min(columns.length - 1, Math.max(0, b.fc + d[1])),
                };
            });
            return;
        }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") {
            const key = `${rowIdx}:${colIdx}`;
            const again = ctrlARef.current?.key === key && Date.now() - (ctrlARef.current?.t || 0) < 1200;
            ctrlARef.current = { key, t: Date.now() };
            if (!el.value || again) { e.preventDefault(); setSel({ ar: 0, ac: 0, fr: lastRow, fc: lastCol }); }
            return;
        }
        if (isMulti && (e.key === "Delete" || e.key === "Backspace")) { e.preventDefault(); clearRect(rect); return; }
        if (e.key === "Escape") { setSel({ ar: rowIdx, ac: colIdx, fr: rowIdx, fc: colIdx }); return; }
        if (e.key === "ArrowUp" || (e.key === "Enter" && e.shiftKey)) { e.preventDefault(); focusCell(rowIdx - 1, colIdx); }
        else if (e.key === "ArrowDown" || e.key === "Enter") {
            e.preventDefault();
            if (isLast) addNewAndFocus(colIdx); else focusCell(rowIdx + 1, colIdx);
        } else if (e.key === "ArrowLeft") {
            if (atStart) { e.preventDefault(); focusCell(rowIdx, colIdx - 1); }
        } else if (e.key === "ArrowRight") {
            if (atEnd) { e.preventDefault(); focusCell(rowIdx, colIdx + 1); }
        } else if (e.key === "Tab") {
            e.preventDefault();
            if (e.shiftKey) {
                if (!focusCell(rowIdx, colIdx - 1)) focusCell(rowIdx - 1, columns.length - 1);
            } else if (!focusCell(rowIdx, colIdx + 1)) {
                if (isLast) addNewAndFocus(0); else focusCell(rowIdx + 1, 0);
            }
        }
    };

    // After a new row is auto-created, move the cursor into it (same column).
    useEffect(() => {
        if (pendingFocusColRef.current == null) return;
        const col = pendingFocusColRef.current;
        pendingFocusColRef.current = null;
        requestAnimationFrame(() => focusCell(filtered.length - 1, col));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtered.length]);

    const addColumn = () => {
        const name = window.prompt(t("ot_new_col"));
        if (!name || !name.trim()) return;
        const key = `col_${Math.random().toString(36).slice(2, 8)}`;
        setColumns((prev) => [...prev, { key, label: name.trim(), type: "text", width: 120, custom: true }]);
    };
    const deleteColumn = (key) => setColumns((prev) => (key === "area" || key === "price"
        ? prev.map((c) => (c.key === key ? { ...c, hidden: true } : c))
        : prev.filter((c) => c.key !== key)));
    const showColumn = (key) => setColumns((prev) => prev.map((c) => (c.key === key ? { ...c, hidden: false } : c)));

    // Pointer-based column reordering (reliable across browsers, with a visible drop indicator).
    const startColDrag = (e, i, key) => {
        if (editingKey) return;
        e.preventDefault();
        dragStateRef.current = { fromIdx: i, key, startX: e.clientX, active: false, shift: e.shiftKey };
    };
    useEffect(() => {
        const move = (e) => {
            const s = dragStateRef.current;
            if (s && !s.active && Math.abs(e.clientX - s.startX) > 5) { s.active = true; setDragKey(s.key); }
        };
        const up = () => {
            const s = dragStateRef.current;
            dragStateRef.current = null;
            if (s && !s.active) headerClickRef.current?.(s.fromIdx, s.shift);
            if (s && s.active && overIdx != null && overIdx !== s.fromIdx) {
                const toKey = columns[overIdx]?.key;
                setColumns((prev) => {
                    const arr = [...prev];
                    const from = arr.findIndex((c) => c.key === s.key);
                    const [m] = arr.splice(from, 1);
                    const to = arr.findIndex((c) => c.key === toKey);
                    arr.splice(overIdx > s.fromIdx ? to + 1 : to, 0, m);
                    return arr;
                });
            }
            setDragKey(null); setOverIdx(null);
        };
        window.addEventListener("mousemove", move);
        window.addEventListener("mouseup", up);
        return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    }, [overIdx, setColumns, columns]);
    const startResize = (key, startW) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        resizeRef.current = { key, startX: e.clientX, startW: startW || 100 };
    };

    const bubblePagesForRow = (r) => {
        const w = Number(r.width);
        const h = Number(r.height);
        if (!w || !h) return [];
        const n = Math.max(1, parseInt(r.qty, 10) || 1);
        const counts = {};
        for (let i = 0; i < n; i++) {
            const off = (unitOffsets || {})[`${r.id}:${i}`];
            const pg = off?.page || trayPage || 1;
            counts[pg] = (counts[pg] || 0) + 1;
        }
        return Object.entries(counts).map(([pg, c]) => ({ page: Number(pg), count: c })).sort((a, b) => a.page - b.page);
    };

    const totalWidth = 32 + columns.reduce((a, c) => a + (c.width || 100), 0) + 120;

    return (
        <div className="h-full flex flex-col">
            {/* Table toolbar */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-[#1e293b] bg-[#111827] flex-wrap">
                <div className="relative flex-1 min-w-[140px]">
                    <Search className="w-4 h-4 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <Input data-testid="order-table-search" value={search} onChange={(e) => setSearch(e.target.value)}
                        placeholder={t("ot_search_ph")} className="pl-8 h-8 bg-[#0b0f17] border-[#1e293b] text-slate-100 text-sm" />
                </div>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger data-testid="order-table-filter-type" className="w-32 h-8 bg-[#0b0f17] border-[#1e293b] text-slate-200 text-sm">
                        <SelectValue placeholder={t("ot_all_types")} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111827] border-[#1e293b] text-slate-100">
                        <SelectItem value="__all__">{t("ot_all_types")}</SelectItem>
                        {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Button data-testid="toggle-filters" size="sm" variant={showFilters ? "default" : "outline"} onClick={() => setShowFilters((s) => !s)}
                    className={showFilters ? "h-8 bg-blue-600 hover:bg-blue-500 text-white" : "h-8 bg-[#0b0f17] border-[#1e293b] text-slate-200 hover:bg-[#182232]"}>
                    <Filter className="w-4 h-4 mr-1" /> {t("ot_filters")}
                </Button>
                {activeValFilters > 0 && (
                    <button data-testid="clear-all-col-filters" onClick={() => setValFilters({})}
                        className="h-8 px-2 rounded border border-blue-500/50 bg-blue-500/10 text-xs font-mono text-blue-200 hover:bg-blue-500/20 transition-colors">
                        {t("cf_clear_all_filters", { n: activeValFilters })} · {filtered.length}/{rows.length}
                    </button>
                )}
                <Button data-testid="add-column-button" size="sm" variant="outline" onClick={addColumn}
                    className="h-8 bg-[#0b0f17] border-[#1e293b] text-slate-200 hover:bg-[#182232]">
                    <Columns3 className="w-4 h-4 mr-1" /> {t("ot_column")}
                </Button>
                {hiddenCols.map((c) => (
                    <button key={c.key} data-testid={`show-col-${c.key}`} onClick={() => showColumn(c.key)} title={t("ot_show_col")}
                        className="h-8 px-2 rounded border border-dashed border-[#334155] text-xs font-mono text-slate-400 hover:text-white hover:border-blue-500 transition-colors">+ {c.label}</button>
                ))}
                <div className="flex items-center gap-1.5 px-2 h-8 rounded border border-[#1e293b] bg-[#0b0f17]" title={t("ot_blind_adj")}>
                    <span className="text-[10px] font-mono uppercase tracking-wider" style={{ color: BLIND_ACCENT }}>{t("ot_blind_adj")}</span>
                    <span className="text-[10px] font-mono text-slate-500">{t("ot_blind_w")}</span>
                    <OffsetInput testId="blind-offset-width" value={Number(blindOffsets?.width) || 0} title={t("ot_blind_w")}
                        onCommit={(v) => setBlindOffsets((p) => ({ ...p, width: v ?? 0 }))}
                        className="w-16 h-6 border border-[#1e293b] !bg-[#111827]" />
                    <span className="text-[10px] font-mono text-slate-500">{t("ot_blind_h")}</span>
                    <OffsetInput testId="blind-offset-height" value={Number(blindOffsets?.height) || 0} title={t("ot_blind_h")}
                        onCommit={(v) => setBlindOffsets((p) => ({ ...p, height: v ?? 0 }))}
                        className="w-16 h-6 border border-[#1e293b] !bg-[#111827]" />
                </div>
                <label data-testid="only-corrected-filter" className={`flex items-center gap-1.5 px-2 h-8 rounded border cursor-pointer text-xs font-mono select-none ${onlyCorrected ? "border-rose-500/60 text-rose-300 bg-rose-500/10" : "border-[#1e293b] text-slate-400"}`}>
                    <input data-testid="only-corrected-checkbox" type="checkbox" checked={onlyCorrected} onChange={(e) => setOnlyCorrected(e.target.checked)} className="accent-rose-500" />
                    {t("ot_only_corrected")}
                </label>
                <Button data-testid="order-table-add-row" size="sm" onClick={onAddRow}
                    className="h-8 bg-blue-600 hover:bg-blue-500 text-white">
                    <Plus className="w-4 h-4 mr-1" /> {t("ot_row")}
                </Button>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto relative focus:outline-none" data-testid="order-table" ref={tableRef} tabIndex={-1}
                onKeyDown={(e) => {
                    if (e.target !== tableRef.current || !rect) return;
                    if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); clearRect(rect); }
                }}
                onCopy={(e) => handleCopy(e, false)} onCut={(e) => handleCopy(e, true)} onPaste={handlePaste}>
                {antsBox && <div data-testid="copy-marquee" className="marching-ants" style={antsBox} />}
                <table className="border-collapse table-fixed" style={{ width: totalWidth }}>
                    <thead className="sticky top-0 z-10 bg-[#0b0f17]">
                        <tr>
                            <th className="border-b border-r border-[#1e293b] px-1 py-2" style={{ width: 32 }}>
                                <div className="flex justify-center">
                                    <button data-testid="parking-toggle" data-flash-count={flashIds?.length || 0}
                                        onClick={() => (flashIds?.length ? onClearFlashes() : onTogglePaused())}
                                        title={flashIds?.length ? t("ot_clear_flashes", { n: flashIds.length }) : paused ? t("ot_park_on") : t("ot_park_off")}
                                        className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${flashIds?.length ? "bg-white border-amber-300 locate-dot-on" : paused ? "bg-white border-white shadow-[0_0_6px_rgba(255,255,255,0.6)]" : "bg-transparent border-slate-500 hover:border-white"}`}>
                                        {paused && <span className="w-1.5 h-1.5 rounded-full bg-slate-900" />}
                                    </button>
                                </div>
                            </th>
                            {columns.map((c, i) => {
                                const accent = AI_COL_ACCENT[c.key] || (c.calc ? BLIND_ACCENT : null);
                                const isDragging = dragKey === c.key;
                                const isOver = dragKey && overIdx === i && !isDragging;
                                const thStyle = { width: c.width };
                                const shadows = [];
                                if (accent) shadows.push(`inset 0 3px 0 0 ${accent}`);
                                if (isOver) shadows.push("inset 3px 0 0 0 #3b82f6");
                                if (shadows.length) thStyle.boxShadow = shadows.join(", ");
                                if (isDragging) thStyle.opacity = 0.4;
                                const label = c.calc === "blindW" || c.calc === "blindH" ? t(c.calc === "blindW" ? "ot_blind_w" : "ot_blind_h") : c.label;
                                const corr = c.calc === "blindW" ? corrCount.W : c.calc === "blindH" ? corrCount.H : null;
                                return (
                                <th key={c.key} style={thStyle}
                                    onMouseEnter={() => dragStateRef.current?.active && setOverIdx(i)}
                                    className="relative border-b border-r border-[#1e293b] p-0 select-none group">
                                    <div className="flex items-center h-full px-2 py-2 gap-1 cursor-grab active:cursor-grabbing"
                                        onMouseDown={(e) => startColDrag(e, i, c.key)}>
                                        {editingKey === c.key ? (
                                            <input autoFocus defaultValue={c.label}
                                                data-testid={`col-header-edit-${c.key}`}
                                                onClick={(e) => e.stopPropagation()}
                                                onBlur={(e) => commitLabel(c.key, e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
                                                    if (e.key === "Escape") { setEditingKey(null); }
                                                }}
                                                className="flex-1 min-w-0 text-left text-xs font-semibold uppercase tracking-wider bg-[#0b0f17] text-slate-100 font-mono px-1 py-0.5 rounded border border-blue-500/60 focus:outline-none" />
                                        ) : (
                                            <span data-testid={`col-header-${c.key}`}
                                                onDoubleClick={() => { if (!c.calc) setEditingKey(c.key); }}
                                                title={c.calc ? label : t("ot_rename_col")}
                                                style={accent ? { color: accent } : undefined}
                                                className="flex-1 text-left text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono truncate">
                                                {label}
                                            </span>
                                        )}
                                        {corr !== null && (
                                            <span data-testid={`corr-count-${c.calc}`} title={t("ot_corr_title", { n: corr })}
                                                className={`shrink-0 text-[10px] font-mono px-1.5 rounded-full border ${corr ? "text-rose-300 border-rose-500/40 bg-rose-500/10" : "text-emerald-300 border-emerald-500/30 bg-emerald-500/10"}`}>{corr} {t("ot_corr_short")}</span>
                                        )}
                                        {showFilters && <ColumnFilter colKey={c.key} label={label} getValues={() => filterValues(c)} selected={valFilters[c.key]}
                                            onChange={(s) => setValFilters((p) => { const n = { ...p }; if (s) n[c.key] = s; else delete n[c.key]; return n; })} />}
                                        {c.custom && (
                                            <button data-testid={`col-delete-${c.key}`}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onClick={() => deleteColumn(c.key)}
                                                className="text-slate-600 hover:text-rose-400 opacity-0 group-hover:opacity-100" title={t("ot_del_col")}>
                                                <X className="w-3 h-3" />
                                            </button>
                                        )}
                                    </div>
                                    <div onMouseDown={startResize(c.key, c.width)} data-testid={`col-resize-${c.key}`}
                                        className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-blue-500/70" />
                                </th>
                                );
                            })}
                            <th className="sticky right-0 z-20 border-b border-l border-[#1e293b] bg-[#0b0f17] px-2 py-2 text-right text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono" style={{ width: 120 }}>
                                {t("ot_actions")}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && (
                            <tr><td colSpan={columns.length + 2} className="text-center text-slate-500 py-10 text-sm">
                                {t("ot_no_rows")}
                            </td></tr>
                        )}
                        {filtered.map((r, idx) => {
                            const gc = rowColors[r.id];
                            const isActive = r.id === activeRowId;
                            const places = bubblePagesForRow(r);
                            const totalBubbles = places.reduce((a, p) => a + p.count, 0);
                            return (
                                <tr key={r.id} ref={(el) => (rowRefs.current[r.id] = el)}
                                    data-testid={`order-table-row-${r.id}`}
                                    onClick={() => setActiveRowId(r.id)}
                                    className={`border-b border-[#182232] cursor-pointer transition-colors ${isActive ? "bg-blue-600/15" : "hover:bg-[#151e2e]"}`}
                                    style={isActive ? { boxShadow: "inset 3px 0 0 0 #3b82f6" } : {}}>
                                    <td className="px-1 py-1 text-center border-r border-[#182232] cursor-e-resize" style={{ width: 32 }}
                                        data-testid={`row-select-${r.id}`} onMouseDown={(e) => { if (e.button === 0) { e.preventDefault(); selectRowAt(idx, e.shiftKey); } }}>
                                        {r.ai?.needsReview ? (
                                            <AlertTriangle data-testid={`needs-review-${r.id}`} className="w-3.5 h-3.5 text-amber-400 inline-block" title={t("ai_needs_review")} />
                                        ) : (
                                            <button type="button" data-testid={`locate-bubble-${r.id}`}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onClick={(e) => { e.stopPropagation(); setActiveRowId(r.id); onLocate(r.id, false); }}
                                                onDoubleClick={(e) => { e.stopPropagation(); onLocate(r.id, true); }}
                                                disabled={!gc} data-active={flashIds?.includes(r.id) ? "1" : "0"}
                                                className={`inline-block w-3.5 h-3.5 rounded-full border border-white/20 cursor-pointer transition-transform hover:scale-125 hover:ring-2 hover:ring-white/60 disabled:cursor-default disabled:hover:scale-100 disabled:hover:ring-0 align-middle ${flashIds?.includes(r.id) ? "locate-dot-on" : ""}`}
                                                style={{ backgroundColor: gc?.hex || "transparent", borderColor: gc ? gc.hex : "#334155" }}
                                                title={gc ? `${r.blindPos || r.position || ""} — ${t("ot_locate_hint")}` : ""} />
                                        )}
                                    </td>
                                    {columns.map((c, colIdx) => {
                                        if (c.calc === "area") {
                                            const da = cellDeco(idx, colIdx, null);
                                            const a = rowArea(r);
                                            return (
                                                <td key={c.key} {...cellProps(idx, colIdx)} className={`relative px-1 py-0.5 border-r border-[#182232] ${da.cls}`} style={{ width: c.width, ...da.style }}>
                                                    <span data-testid={`cell-area-${r.id}`} className="block text-sm font-mono text-slate-300 px-1.5 py-1 select-none">{a ?? "—"}</span>
                                                    {fillHandle(idx, colIdx, c, r)}
                                                </td>
                                            );
                                        }
                                        if (c.calc === "blindW") {
                                            const effW = effOff(r, "W");
                                            const bw = Number(r.width);
                                            const blindW = Number.isFinite(bw) && bw > 0 ? bw + effW : null;
                                            const dW = cellDeco(idx, colIdx, `inset 3px 0 0 0 ${effW ? "#ef4444" : BLIND_ACCENT + "44"}`);
                                            return (
                                                <td key={c.key} {...cellProps(idx, colIdx)} className={`relative px-1 py-0.5 border-r border-[#182232] ${dW.cls}`} style={{ width: c.width, ...dW.style }}>
                                                    <div className="flex items-center gap-1">
                                                        <span data-testid={`blind-width-${r.id}`} className="flex-1 text-sm font-mono text-teal-300 px-1">{blindW ?? "—"}</span>
                                                        <OffsetInput testId={`blind-offw-${r.id}`} value={effW} title={t("ot_blind_adj")} onFocus={() => setActiveRowId(r.id)}
                                                            onKeyDown={(e) => handleCellKeyDown(e, idx, colIdx)}
                                                            onCommit={(v) => { setRowOffset(r.id, "blindOffW", v); collapseTo(idx, colIdx); }} />
                                                    </div>
                                                    {fillHandle(idx, colIdx, c, r)}
                                                </td>
                                            );
                                        }
                                        if (c.calc === "blindH") {
                                            const effH = effOff(r, "H");
                                            const bh = Number(r.height);
                                            const blindH = Number.isFinite(bh) && bh > 0 ? bh + effH : null;
                                            const dH = cellDeco(idx, colIdx, `inset 3px 0 0 0 ${effH ? "#ef4444" : BLIND_ACCENT + "44"}`);
                                            return (
                                                <td key={c.key} {...cellProps(idx, colIdx)} className={`relative px-1 py-0.5 border-r border-[#182232] ${dH.cls}`} style={{ width: c.width, ...dH.style }}>
                                                    <div className="flex items-center gap-1">
                                                        <span data-testid={`blind-height-${r.id}`} className="flex-1 text-sm font-mono text-teal-300 px-1">{blindH ?? "—"}</span>
                                                        <OffsetInput testId={`blind-offh-${r.id}`} value={effH} title={t("ot_blind_adj")} onFocus={() => setActiveRowId(r.id)}
                                                            onKeyDown={(e) => handleCellKeyDown(e, idx, colIdx)}
                                                            onCommit={(v) => { setRowOffset(r.id, "blindOffH", v); collapseTo(idx, colIdx); }} />
                                                    </div>
                                                    {fillHandle(idx, colIdx, c, r)}
                                                </td>
                                            );
                                        }
                                        const dc = cellDeco(idx, colIdx, AI_COL_ACCENT[c.key] ? `inset 3px 0 0 0 ${AI_COL_ACCENT[c.key]}44` : null);
                                        return (
                                        <td key={c.key} {...cellProps(idx, colIdx)}
                                            data-testid={`td-${c.key}-${r.id}`}
                                            className={`relative px-1 py-0.5 border-r border-[#182232] ${dc.cls}`}
                                            style={{ width: c.width, ...dc.style }}>
                                            {getColumnOptions(c) ? (
                                            <ComboCell testId={`cell-${c.key}-${r.id}`} data-cell="1" options={getColumnOptions(c)}
                                                value={r[c.key] ?? ""}
                                                onClick={(e) => e.stopPropagation()}
                                                onFocus={() => setActiveRowId(r.id)}
                                                onKeyDown={(e) => handleCellKeyDown(e, idx, colIdx)}
                                                onChange={(v) => { update(r.id, c.key, v, c.type); collapseTo(idx, colIdx); }}
                                                className="w-full bg-transparent text-sm text-slate-200 px-1.5 py-1 rounded focus:bg-[#0b0f17] focus:outline-none focus:ring-2 focus:ring-blue-500/70 font-sans" />
                                            ) : (
                                            <input data-testid={`cell-${c.key}-${r.id}`} data-cell="1"
                                                type={c.type === "number" ? "number" : "text"}
                                                value={c.key === "position" ? (r.position || String(idx + 1)) : (r[c.key] ?? "")}
                                                readOnly={c.key === "position"}
                                                onClick={(e) => e.stopPropagation()}
                                                onFocus={() => setActiveRowId(r.id)}
                                                onKeyDown={(e) => handleCellKeyDown(e, idx, colIdx)}
                                                onChange={(e) => { update(r.id, c.key, e.target.value, c.type); collapseTo(idx, colIdx); }}
                                                className="w-full bg-transparent text-sm text-slate-200 px-1.5 py-1 rounded focus:bg-[#0b0f17] focus:outline-none focus:ring-2 focus:ring-blue-500/70 font-sans" />
                                            )}
                                            {fillHandle(idx, colIdx, c, r)}
                                        </td>
                                        );
                                    })}
                                    <td className="sticky right-0 z-10 border-l border-[#182232] px-2 py-1" style={{ width: 120, backgroundColor: isActive ? "#101a2b" : "#0b0f17" }}>
                                        <div className="flex items-center gap-0.5 justify-end">
                                            {places.length > 0 && (
                                                <Popover open={pagesOpen === r.id} onOpenChange={(o) => setPagesOpen(o ? r.id : null)}>
                                                    <PopoverTrigger asChild>
                                                        <button data-testid={`row-pages-${r.id}`} onClick={(e) => e.stopPropagation()}
                                                            className="flex items-center gap-0.5 text-[10px] font-mono mr-1 px-1.5 h-6 rounded border border-[#1e293b] hover:bg-[#182232]"
                                                            style={{ color: gc?.hex || "#94a3b8" }}
                                                            title={t("ot_bubble_pages")}>
                                                            <MapPin className="w-3 h-3" />{totalBubbles}
                                                            {places.length > 1 && <span className="text-slate-500">·{places.length}L</span>}
                                                        </button>
                                                    </PopoverTrigger>
                                                    <PopoverContent align="end" className="w-56 p-1.5 bg-[#111827] border-[#1e293b] text-slate-100" onClick={(e) => e.stopPropagation()}>
                                                        {(() => {
                                                            const decks = [...new Set(String(r.blindPos || "").split(/[,;]+/).map((s) => s.trim()).filter(Boolean))];
                                                            return (<>
                                                                <div data-testid={`row-nav-summary-${r.id}`} className="px-1.5 py-1 mb-1 rounded bg-[#0b0f17] border border-[#1e293b] text-xs font-mono space-y-0.5">
                                                                    <div className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: gc?.hex || "#a855f7" }} /><span className="text-slate-500">{t("ot_deck")}:</span><span className="truncate">{decks.join(", ") || "—"}</span></div>
                                                                    <div className="flex items-center gap-1.5 text-slate-300"><FileText className="w-3 h-3 text-slate-400 shrink-0" /><span className="text-slate-500">{t("ot_pages")}:</span><span className="truncate">{places.map((p) => p.page).join(", ")}</span></div>
                                                                </div>
                                                                <div className="text-[10px] font-mono text-slate-400 px-1.5 py-1 uppercase tracking-wider">{t("ot_deck")}</div>
                                                                {decks.length ? decks.map((d) => (
                                                                    <button key={d} data-testid={`goto-deck-${r.id}-${d}`} onClick={() => { setPagesOpen(null); onDeck(d, r.id); }}
                                                                        className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded hover:bg-fuchsia-600/25 text-sm font-mono transition-colors">
                                                                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: gc?.hex || "#a855f7" }} /> {d}
                                                                    </button>
                                                                )) : <div className="px-2 py-1 text-xs text-slate-500">—</div>}
                                                            </>);
                                                        })()}
                                                        <div className="text-[10px] font-mono text-slate-400 px-1.5 py-1 mt-1 uppercase tracking-wider border-t border-[#1e293b]">{t("ot_pages")}</div>
                                                        {places.map((p) => (
                                                            <button key={p.page} data-testid={`goto-page-${r.id}-${p.page}`}
                                                                onClick={() => { setPagesOpen(null); onGoToPage(p.page, r.id); }}
                                                                className="w-full flex items-center justify-between px-2 py-1.5 rounded hover:bg-blue-600/25 text-sm transition-colors">
                                                                <span className="flex items-center gap-1.5"><ArrowRight className="w-3.5 h-3.5 text-blue-400" /> {(pageNames || {})[p.page] || t("pv_page", { n: p.page })}</span>
                                                                <span className="text-xs font-mono text-slate-400">{p.count} {t("ot_vnt")}</span>
                                                            </button>
                                                        ))}
                                                    </PopoverContent>
                                                </Popover>
                                            )}
                                            <button data-testid={`copy-row-${r.id}`} onClick={(e) => { e.stopPropagation(); onCopyRow(r.id); }}
                                                className="text-slate-500 hover:text-blue-400 p-1 rounded hover:bg-blue-500/10" title={t("ot_copy")}>
                                                <Copy className="w-3.5 h-3.5" />
                                            </button>
                                            <button data-testid={`delete-row-${r.id}`} onClick={(e) => { e.stopPropagation(); onDeleteRow(r.id); }}
                                                className="text-slate-500 hover:text-rose-400 p-1 rounded hover:bg-rose-500/10" title={t("ot_delete")}>
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="px-3 py-1.5 border-t border-[#1e293b] bg-[#0b0f17] text-xs text-slate-500 font-mono flex items-center justify-between gap-4">
                <span className="whitespace-nowrap">{t("ot_showing", { f: filtered.length, t: rows.length, c: columns.length })}</span>
                <SelectionStats stats={stats} t={t} />
            </div>
            <AlertDialog open={!!pendingPaste} onOpenChange={(o) => { if (!o) setPendingPaste(null); }}>
                <AlertDialogContent data-testid="paste-rows-dialog" className="bg-[#111827] border-[#1e293b] text-slate-100">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("ot_paste_rows_title")}</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">{t("ot_paste_rows_desc", { n: pendingPaste?.need ?? 0 })}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="paste-existing-only" className="bg-transparent border-[#334155] text-slate-200 hover:bg-[#1e293b] hover:text-white"
                            onClick={() => applyPaste(pendingPaste.writes, pendingPaste.target, false)}>{t("ot_paste_existing")}</AlertDialogCancel>
                        <AlertDialogAction data-testid="paste-create-rows" className="bg-blue-600 hover:bg-blue-500 text-white"
                            onClick={() => applyPaste(pendingPaste.writes, pendingPaste.target, true)}>{t("ot_paste_create")}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
